/**
 * The CAIRA masterclasses, one list per level, as shown on the homepage
 * (AiReadiness.tsx, AppliedAi.tsx, OperatingModel.tsx). Kept here so other
 * pages (e.g. the hackathon's Levels section) show exactly the same courses
 * instead of their own copies.
 *
 * `thumbnail` is a bare ImageKit path under the level's `ik` folder; build
 * the URL with ./thumbnails. `title` is the card text straight from the sheet.
 */

export type CairaCourse = {
  id: string;
  /** The card heading, straight from the sheet. Also used for the image alt. */
  title: string;
  /** Topic label under the title. */
  category: string;
  /** Bare ImageKit path — the transform is appended at render, see ./thumbnails. */
  thumbnail: string;
  videoUrl: string;
};

export type CairaCourseLevel = {
  n: 1 | 2 | 3;
  /** Section heading on the homepage. */
  title: string;
  /** The line under the heading on the homepage. */
  sub: string;
  /** ImageKit folder the thumbnails live in. */
  ik: string;
  courses: CairaCourse[];
};

export const AI_MINDSET: CairaCourseLevel = {
  n: 1,
  title: "AI Mindset, Skillset & Toolset",
  sub: "Building AI Agents & Workflows on the Microsoft Stack (Copilot, Power Automate, Copilot Studio, Power BI)",
  ik: "https://ik.imagekit.io/mileseducation/miles_website/home_2026/ai-readiness",
  courses: [
    {
      id: "microsoft-copilot-dawn-of-the-itelligent",
      category: "Information Technology",
      title:
        "Explore how Microsoft 365 Copilot boosts productivity for accounting professionals with AI-powered features across Word, Excel, Teams and more",
      thumbnail: "COPILOT%20CHRIS%20STEGH.jpg",
      videoUrl: "https://www.youtube.com/watch?v=kRB9TMP9RBc",
    },
    {
      id: "building-a-future-ready-ai-toolkit",
      category: "Information Technology",
      title:
        "Business leaders face overwhelming AI change, but those who build agentic and multimodal skills now create lasting professional advantage.",
      thumbnail: "BUILDING%20A%20FUTURE%20READY%20AI%20TOOLKIT.jpg",
      videoUrl: "https://www.youtube.com/watch?v=EItrhAmOTLE",
    },
    {
      id: "power-bi-for-finance",
      category: "Information Technology",
      title:
        "Build finance-ready Power BI models, metrics, and dashboards with Cristiano Galvao for clearer, faster, decision-ready reporting.",
      thumbnail: "POWER%20BI%20FOR%20FINANCE%20CRISTIANO%20GALVAO.jpg",
      videoUrl: "https://www.youtube.com/watch?v=XtXNnlnRkZA",
    },
    {
      id: "reclaim-10-hours-a-week-using-chatgpt",
      category: "Information Technology",
      title:
        "Use GPT as your everyday thought partner to streamline tasks, improve focus, and reclaim hours for high-value work.",
      thumbnail:
        "reclaim%2010%20hours%20a%20week%20using%20Chatgpt%20Landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=LgKROuUo1oE",
    },
    {
      id: "advanced-excel-masterclass",
      category: "Information Technology",
      title:
        "Learn to transform Excel into a scalable finance platform with data models, automation, advanced formulas, and AI-assisted analysis.",
      thumbnail: "ADVANCED%20EXCEL%20MASTERCLASS%20-%20CRISTIANO%20GALVAO.jpg",
      videoUrl: "https://www.youtube.com/watch?v=S1mUTIFUH24",
    },
    {
      id: "power-automation-engineering-the-invisib",
      category: "Information Technology",
      title:
        "Learn how to design, build, and scale intelligent workflows using Power Automate.",
      thumbnail: "POWER%20AUTOMATE%20-%20GRIFFIN%20LICKFELDT.jpg",
      videoUrl: "https://www.youtube.com/watch?v=KgrAqIQfzAU",
    },
    {
      id: "transform-data-into-action-with-power-bi",
      category: "Information Technology",
      title:
        "Take control of your data with Power BI. Dr. Mfon Akpan reveals how to transform raw data into insights and create reports and dashboards.",
      thumbnail:
        "Transform%20Data%20into%20Action%20with%20Power%20BI%20with%20Mfon%20Akpan.png",
      videoUrl: "https://www.youtube.com/watch?v=SfYVt8orr3M",
    },
    {
      id: "the-accoutant-as-architech-of-intelligen",
      category: "Information Technology",
      title:
        "Learn how AI, machine learning, and agentic systems are transforming accounting workflows, governance, and enterprise operations.",
      thumbnail:
        "THE%20ACCOUNTANT%20AS%20ARCHITECT%20OF%20INTELLIGENCE%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=gJpGwyEhqxQ",
    },
    {
      id: "microsoft-copilot-a-beginner-s-pathway",
      category: "Information Technology",
      title:
        "Master Microsoft 365 Copilot as your secure AI partner across Outlook, Excel, Word and PowerPoint to research, analyze and deliver faster.",
      thumbnail: "COPILOT%20-%20Teresa%20Cyrus.jpg",
      videoUrl: "https://www.youtube.com/watch?v=Yo_LM1CeLyg",
    },
    {
      id: "ai-for-accountants-friend-or-foe",
      category: "Information Technology",
      title:
        "Master AI in accounting with Dr. Scott Dell. Explore tools, risks, and strategies to thrive in an AI-driven finance world.",
      thumbnail:
        "AI%20for%20Accountants_%20Friend%20or%20Foe%20with%20Dr.%20Scott%20Dell%20(1)%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=DtIb56Mdp_g",
    },
    {
      id: "data-analytics-for-better-decisions",
      category: "Information Technology",
      title:
        "Turn data into decisions: ask the right questions, choose the right visuals, build dashboards, and drive meaningful action.",
      thumbnail: "Data%20Analytics%20for%20Better%20Decisions%20with%20Joe.png",
      videoUrl: "https://www.youtube.com/watch?v=ZA6lsejwcAo",
    },
    {
      id: "next-gen-accounting",
      category: "Information Technology",
      title:
        "Workflows vs agents for finance: build n8n automations, then deploy agents for month-end QA and toward continuous close. Layered roadmap.",
      thumbnail:
        "Next-Gen%20Accounting-%20AI%20Workflows%20&%20Agents%20with%20Alex%20Lee.png",
      videoUrl: "https://www.youtube.com/watch?v=kvslxB6leBk",
    },
    {
      id: "ai-agents-the-new-electricity",
      category: "Information Technology",
      title:
        "Learn how Agentic AI transforms business by moving from chatbots to autonomous agents, with real use cases, risks, and future trends.",
      thumbnail:
        "AI%20Agents%20in%20Accounting%20_%20The%20New%20Electricity%20with%20Octavio%20_OJ_%20Laos.png",
      videoUrl: "https://www.youtube.com/watch?v=DUBp-QU-0Oc",
    },
    {
      id: "ai-it-s-all-about-the-prompt",
      category: "Information Technology",
      title:
        "Master prompt engineering with Dr. Scott Dell, exploring AI-driven tools, advanced techniques, and practical applications for success.",
      thumbnail: "AI-The%20Art%20of%20The%20Prompt%20with%20Scott%20Dell.jpg",
      videoUrl: "https://www.youtube.com/watch?v=5uJPB2wo1S0",
    },
  ],
};

export const APPLIED_AI: CairaCourseLevel = {
  n: 2,
  title: "Applied AI Across Functions",
  sub: "Deploying Agentic AI Solutions in Audit, Tax & CAS/CFO Teams - The Build vs. Buy decision",
  ik: "https://ik.imagekit.io/mileseducation/miles_website/home_2026/ai-operating-model",
  courses: [
    {
      id: "power-automate-endgame-for-manual-finance",
      category: "Information Technology",
      title:
        "Learn how Power Automate helps tax teams automate deadlines, document checks, data requests, and review task workflows.",
      thumbnail: "POWER%20AUTOMATE%20ENDGAME%20-%20GRIFFIN%20LICKFELDT.jpg",
      videoUrl: "https://www.youtube.com/watch?v=KgrAqIQfzAU",
    },
    {
      id: "audit-breakthough-with-copilot",
      category: "Accounting",
      title:
        "Explore how Chris Stegh uses Microsoft Copilot to streamline audit workflows, strengthen judgment, and improve audit communication.",
      thumbnail: "The%20Audit%20Breakthrough%20with%20Copilot.webp",
      videoUrl: "https://www.youtube.com/watch?v=3fc1n0Y3BU4",
    },
    {
      id: "build-to-digitize",
      category: "Accounting",
      title:
        "Reimagine accounting with AI - automate the grind, close books in real time, and turn financial data into instant business insight.",
      thumbnail: "Built%20to%20Digitize%20with%20Jeff%20Seibert.png",
      videoUrl: "https://www.youtube.com/watch?v=tKt5Kqxo2VM",
    },
    {
      id: "super-charge-you-practice",
      category: "Information Technology",
      title:
        "Learn how AI-powered practice management tools like Karbon enhance workflow, communication, and client management for increased efficiency.",
      thumbnail: "SUPERCHARGE%20PRACTICE%20ian%20vacine.jpg",
      videoUrl: "https://www.youtube.com/watch?v=_k5PeOuORxY",
    },
    {
      id: "audit-beyound-spreadsheets",
      category: "Auditing",
      title:
        "Learn how 401(k) auditors can reduce manual work, improve data accuracy, and streamline audit workflows using modern tools and smarter process",
      thumbnail: "AUDIT%20BEYOND%20SPREADSHEET.jpg",
      videoUrl: "https://www.youtube.com/watch?v=tSBj6Ipuhns",
    },
    {
      id: "automated-the-close",
      category: "Accounting",
      title:
        "Harness AI to simplify workflows, cut errors, and elevate your accounting career from execution to leadership.",
      thumbnail: "AUTOMATE%20THE%20CLOSE%20-%20ALEX%20LEE.jpg",
      videoUrl: "https://www.youtube.com/watch?v=L9jfm-Sn_zY",
    },
    {
      id: "ai-meets-erp",
      category: "Computer software & Applications",
      title:
        "Learn how AI plus ERP transforms firm operations, starting with data readiness and ending with AI embedded across the platform.",
      thumbnail: "AI%20MEETS%20ERP.jpg",
      videoUrl: "https://www.youtube.com/watch?v=3MTQIRfFen0",
    },
    {
      id: "ai-empowered-auditing",
      category: "Auditing",
      title:
        "CPA-focused AI series covering tools, ethics, risk control, skill-building, real demos, and practical hacks for audit productivity.",
      thumbnail: "AI-Empowered%20Auditing%20with%20Raymond.jpg",
      videoUrl: "https://www.youtube.com/watch?v=DLuVIO1sc_E",
    },
    {
      id: "investingating-fraud",
      category: "Auditing",
      title:
        "Uncover fraud with data analytics. Learn to design and refine detection models using tools like Benford’s Law, AI, and statistical analysis.",
      thumbnail:
        "Investigating%20Fraud%20with%20Data%20Models%20with%20Robert%20Persichitte.png",
      videoUrl: "https://www.youtube.com/watch?v=NRQS8s9uwns",
    },
    {
      id: "file-to-win-ai-s-power-boost-for-tax",
      category: "Information Technology",
      title:
        "Explore how AI tools are transforming tax and accounting work by boosting productivity, client service, and human connection.",
      thumbnail: "FILED%20TO%20WIN-%20LK%20and%20RD.jpg",
      videoUrl: "https://www.youtube.com/watch?v=tivuVuOXmko",
    },
    {
      id: "ai-tax-specialist-part1",
      category: "Taxes",
      title:
        "Led by Rachel DiFuccia, this course shows how AI redesigns tax workflows with secure automation, human oversight, and review-ready returns.",
      thumbnail: "AI%20TAX%20SPECIALIST%20V1%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=O8TAy4jirF0",
    },
    {
      id: "ai-tax-specialist-part2",
      category: "Taxes",
      title:
        "Rachel DiFuccia shows how RPA, scripting, and AI reshape tax workflows, with explainability, ethics, and human oversight.",
      thumbnail: "AI%20TAX%20SPECIALIST%20V1%20part%202.jpg",
      videoUrl: "https://www.youtube.com/watch?v=O8TAy4jirF0",
    },
    {
      id: "behind-the-build-aiwyn",
      category: "Computer software & Applications",
      title:
        "Ellen Choi shows how Aiwyn modernized billing, payments, and practice of cutting manual work and scaling with AI.",
      thumbnail:
        "Behind%20the%20Build-Case%20Study%20on%20Aiwyn%20with%20Ellen%20Choi.png",
      videoUrl: "https://www.youtube.com/watch?v=Z3-0_B9I5xk",
    },
    {
      id: "securing-your-firm-in-the-age-of-ai",
      category: "Information Technology",
      title:
        "Learn how tax firms can securely adopt AI across client intake, internal workflows, delivery, and agentic automation while maintaining control",
      thumbnail: "SECURING%20YOUR%20FIRM.jpg",
      videoUrl: "https://www.youtube.com/watch?v=AZX8k8UcvUk",
    },
    {
      id: "from-financials-to-advisory-insights",
      category: "Management Services",
      title:
        "Learn how to turn financial data into advisory insights using AI, benchmarking, and structured workflows to guide better client decisions.",
      thumbnail: "From%20Financials%20to%20Advisory%20Insight.jpg",
      videoUrl: "https://www.youtube.com/watch?v=4747LjSqt5Q",
    },
    {
      id: "the-science-behind-standfordtax",
      category: "Taxes",
      title:
        "Explore how modern tax firms use AI-powered intake, prep-ready binders, and workflow automation to reduce friction.",
      thumbnail:
        "THE%20SCIENCE%20BEHIND%20STANFORDTAX%20-%20DAN%20BERENHOLTS.jpg",
      videoUrl: "https://www.youtube.com/watch?v=g89ZWIqq86s",
    },
    {
      id: "the-firm-leader-s-quest",
      category: "Information Technology",
      title:
        "Build a niche-driven, scalable firm by uncovering client pain, validating services, and systemizing workflows for consistent growth today.",
      thumbnail:
        "The%20Firm%20Leader_s%20Quest%20with%20Jetpack%20Workflow.png",
      videoUrl: "https://www.youtube.com/watch?v=OjVEpO5f-7o",
    },
    {
      id: "role-of-technology-in-testing-controls",
      category: "Auditing",
      title:
        "Elevate your audit game! Discover how tech-driven testing can enhance risk management and control assessment by Dr. Roger Mayer.",
      thumbnail:
        "Role%20of%20Technology%20in%20Testing%20Controls%20with%20Roger%20Mayer.png",
      videoUrl: "https://www.youtube.com/watch?v=augBJEahjpQ",
    },
    {
      id: "document-intelligence",
      category: "Computer software & Applications",
      title:
        "Build flows to extract, route, and automate invoices, expenses, and contracts freeing teams for high-value work.",
      thumbnail: "Document%20Intelligence%20for%20Finance%20Leaders.jpg",
      videoUrl: "https://www.youtube.com/watch?v=PZ8LvTWq7xk",
    },
    {
      id: "ai-powered-transformation-in-tax-firms",
      category: "Taxes",
      title:
        "Use AI driven remittance automation to eliminate the triple tax on cash, time and stress while scaling secure, client first tax payment servic",
      thumbnail:
        "AI%20POWERED%20TRANSFORMATIONS%20IN%20TAX%20FIRMS%20-%20SOLON%20ANGEL.jpg",
      videoUrl: "https://www.youtube.com/watch?v=uzMIBYOxtNM",
    },
    {
      id: "agentive-audit-automation",
      category: "Auditing",
      title:
        "See how agentic AI streamlines audit testing, improves quality, and frees auditors to focus on judgment and review.",
      thumbnail: "Agentive%20Audit%20Automation.png",
      videoUrl: "https://www.youtube.com/watch?v=xhlChgfA4OM",
    },
  ],
};

export const OPERATING_MODEL: CairaCourseLevel = {
  n: 3,
  title: "Firm-Wide AI Operating Model",
  sub: "Leading AI Transformation - Governance, Controls, Agent Evaluation & the Deployment Playbook",
  ik: "https://ik.imagekit.io/mileseducation/miles_website/home_2026/applied-ai",
  courses: [
    {
      id: "transforming-client-services",
      category: "Information Technology",
      title:
        "Transform client services with AI powered, human elevated CAS models uniting people, process and technology for scalable, high value advisory.",
      thumbnail: "TRANSFORMING%20CLIENT%20SERVICES%20v2%20landscape%20(1).jpg",
      videoUrl: "https://www.youtube.com/watch?v=hTeo-TTKgjU",
    },
    {
      id: "navigating-accounting-with-ai",
      category: "Information Technology",
      title:
        "Explore how AI transforms accounting by automating tasks, boosting efficiency and enabling professionals to focus on client-focused insights.",
      thumbnail:
        "NAVIGATING%20ACCOUNTING%20WITH%20AI%20-%20AMEL%20EDMOND%20HORIZONTAL%20(1).jpg",
      videoUrl: "https://www.youtube.com/watch?v=E2BYNo4wqNI",
    },
    {
      id: "convergence-of-ai-ip",
      category: "Information Technology",
      title:
        "Learn how firms can package services, monetize IP, harness AI, and attract talent to build sustainable, future-ready practices.",
      thumbnail:
        "The%20Convergence%20of%20AI%20and%20IP%20with%20Gary%20Boomer%20landscape%20(1)%20(1).jpg",
      videoUrl: "https://www.youtube.com/watch?v=s59py8XA68o",
    },
    {
      id: "the-ai-accountant",
      category: "Accounting",
      title:
        "From automation to strategy - how CPAs can harness AI for efficiency, insight, and leadership in the new accounting era.",
      thumbnail:
        "The%20AI%20Accountant%20-%20Adapt,%20Automate,%20Achieve-landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=_-2O1z393RU",
    },
    {
      id: "navigating-cybersecurity-incident",
      category: "Information Technology",
      title:
        "From IT evolution to cyber defense, this course equips you with essential strategies to tackle modern threats and ensure business resilience.",
      thumbnail:
        "NAVIGATING%20A%20CYBERSECURITY%20INCIDENT%20-%20TAMMIE%20HOLLIS%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=bfBo1ZQ-fVo",
    },
    {
      id: "building-responsible-ai-in-cpa-firms",
      category: "Information Technology",
      title:
        "Masterclass on embedding AI into CPA firms through responsible frameworks, risk safeguards, and high-impact, real-world applications.",
      thumbnail:
        "Building%20Responsible%20AI%20in%20CPA%20Firms%20with%20Raymond-landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=xChGkYRpjm0",
    },
    {
      id: "the-state-of-ai-in-accounting",
      category: "Information Technology",
      title:
        "Explore AI’s impact on accounting, key use cases, and insights from Karbon’s reports on AI adoption and training.",
      thumbnail: "The%20State%20of%20AI%20in%20Accounting%20Horizontal.jpg",
      videoUrl: "https://www.youtube.com/watch?v=3fi2T-idmxc",
    },
    {
      id: "build-a-10x-firm-the-ai-exo-system",
      category: "Information Technology",
      title:
        "Rethink growth with ExO + AI: flatten orgs, use LLMs/bots/doc AI to cut busywork, curb partner reliance, and build a 10× firm.",
      thumbnail:
        "Build%20a%2010X%20Firm%20-%20The%20AI%20+%20ExO%20System%20Gary%20Boomer-Horizontal.png",
      videoUrl: "https://www.youtube.com/watch?v=e1epFGC4yz4",
    },
    {
      id: "ai-enablement-for-accounting-firms",
      category: "Accounting",
      title:
        "A practical guide for accounting firms to adopt AI, boost efficiency, manage risks, and transform client service with smart automation",
      thumbnail:
        "AI%20Enablement%20for%20Accounting%20Firms%20with%20Ellen%20Choi-1280%20x%20720.png",
      videoUrl: "https://www.youtube.com/watch?v=dXF5SzbWti8",
    },
    {
      id: "a-strategic-blueprint-for-ai-adoption-in-a",
      category: "Accounting",
      title:
        "AI is here - now it’s about making it work. From strategy to training, tools, use cases, and ROI, learn how to turn AI into everyday results.",
      thumbnail:
        "A%20Strategic%20Blueprint%20for%20AI%20Adoption%20in%20Accounting%20with%20Octavio%20OJ%20Laos-%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=meQr4Z10aSA",
    },
    {
      id: "beyound-the-ai-hype",
      category: "Information Technology",
      title:
        "Professional service leaders navigate AI adoption between competitive risk and governance gaps and build your deployment roadmap.",
      thumbnail: "Beyond%20the%20AI%20Hype%20Horizontal.png",
      videoUrl: "https://www.youtube.com/watch?v=MJYmTiwjSsM",
    },
    {
      id: "the-genius-equation-thriving-in-the-age-of",
      category: "Information Technology",
      title:
        "Professionals in transition: where your capability meets shifting expectations, and judgment becomes the differentiator over execution alone.",
      thumbnail: "THE%20GENIUS%20EQUATION%20landscape%20(1).jpg",
      videoUrl: "https://www.youtube.com/watch?v=O3QZdeUop68",
    },
    {
      id: "the-cfo-s-ai-playbook",
      category: "Finance",
      title:
        "A CFO’s guide to using AI strategically, managing risks, and setting governance to ensure ethical and effective business adoption.",
      thumbnail: "The%20CFO_s%20AI%20Playbook%20with%20Joe%20Oringel.png",
      videoUrl: "https://www.youtube.com/watch?v=Ge_TF-RZOsY",
    },
    {
      id: "the-cpa-s-ai-playbook",
      category: "Information Technology",
      title:
        "Explore how AI is transforming accounting, audit, tax, ethics, and CPA careers while elevating judgment and advisory skills.",
      thumbnail: "The%20CPAs%20AI%20Playbook%20landscape.jpg",
      videoUrl: "https://www.youtube.com/watch?v=-8seXI_FmKU",
    },
  ],
};

/** All three levels in order. */
export const CAIRA_COURSE_LEVELS = [AI_MINDSET, APPLIED_AI, OPERATING_MODEL];
